import Decimal from 'decimal.js';

import { RealtimeLevelPrice } from 'src/type/realtime-level-price';

/**
 * Net (customer-facing) price for one level/product/side, taken from the same
 * Realtime Database node the mobile app reads, plus the source price it was
 * built from.
 *
 * The backend computes net = source + profit, where profit is per baht-weight
 * for gold and per kilogram for silver. Deriving the source as net − profit
 * from the same snapshot keeps both lines consistent with what customers see.
 *
 * Values follow the mobile app's display exactly: silver per gram × 1000 via
 * Decimal (gold-price-card `gramPriceToKg`), then truncated — not rounded — to
 * 2 decimals (app `fNumber(value, 2)`). Gold is per baht-weight, silver per kg.
 */
export type NetPrice = {
  net: number;
  source: number;
  profit: number;
};

const toAppPrecision = (value: Decimal) => value.toDecimalPlaces(2, Decimal.ROUND_DOWN).toNumber();

export function getRealtimeNetPrice(
  realtime: RealtimeLevelPrice | null | undefined,
  type: 'buy' | 'sell',
  isSilver: boolean
): NetPrice | null {
  if (!realtime) return null;

  const perGram = type === 'buy' ? realtime.priceBuyPerGram : realtime.priceSellPerGram;
  const perUnit = type === 'buy' ? realtime.priceBuyPerUnit : realtime.priceSellPerUnit;
  const raw = isSilver ? perGram : perUnit;

  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) return null;

  const netExact = isSilver ? new Decimal(raw).mul(1000) : new Decimal(raw);
  const profitValue = type === 'buy' ? realtime.buyProfit : realtime.sellProfit;
  const profit = typeof profitValue === 'number' && Number.isFinite(profitValue) ? profitValue : 0;

  return {
    net: toAppPrecision(netExact),
    source: toAppPrecision(netExact.minus(profit)),
    profit,
  };
}

/** Latest `updatedAt` across a level → product map, or undefined if none. */
export function getLatestRealtimeUpdate(
  prices: Record<string, Record<string, RealtimeLevelPrice | null> | null> | null | undefined
): number | undefined {
  let latest: number | undefined;
  Object.values(prices ?? {}).forEach((byProduct) => {
    Object.values(byProduct ?? {}).forEach((price) => {
      const at = price?.updatedAt;
      if (typeof at === 'number' && (latest === undefined || at > latest)) latest = at;
    });
  });
  return latest;
}
